/**
 * The `topup-credits-promos` LaunchDarkly JSON flag, read by the promo banner
 * and the Top up Credits modal (SOW 49). The backend
 * (shootsta-backend-private `payments/utils/topupPromo.ts`) reads the same flag
 * at checkout with the same rules, so the "+X free" line here always matches
 * what the workspace receives.
 */
export interface TopupPromo {
  enabled: true;
  bannerText: string;
  buyQuantity: number;
  freeQuantity: number;
}

const isPositiveInteger = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) > 0;

/**
 * Anything that is not an enabled promo with whole, positive quantities
 * reads as no promo.
 */
export function parseTopupPromo(value: unknown): TopupPromo | null {
  if (!value || typeof value !== 'object') return null;

  const { enabled, bannerText, buyQuantity, freeQuantity } = value as Record<
    string,
    unknown
  >;

  if (enabled !== true) return null;
  if (!isPositiveInteger(buyQuantity) || !isPositiveInteger(freeQuantity)) {
    return null;
  }

  return {
    enabled: true,
    bannerText: typeof bannerText === 'string' ? bannerText : '',
    buyQuantity,
    freeQuantity
  };
}

/**
 * One bonus per eligible purchase: buying at least `buyQuantity` earns
 * `freeQuantity` free, however many are bought (buy 3 get 1: 2 → 0, 3 → 1,
 * 6 → 1). The promo terms say one free credit per eligible purchase.
 */
export function promoBonusFor(
  quantity: number,
  promo: TopupPromo | null
): number {
  if (!promo || !Number.isFinite(quantity) || quantity <= 0) return 0;
  return quantity >= promo.buyQuantity ? promo.freeQuantity : 0;
}
